import { buildAggregateResult } from "../analytics/engine/aggregates";
import { groupBy } from "../analytics/engine/groupBy";
import { computeTrend } from "../analytics/engine/trend";
import { computeCorrelation } from "../analytics/engine/correlation";

// ============================================================
// DATA PROFILING + METRICS ENGINE
// ============================================================
// This is the ONLY place either report pulls real numbers from. Every
// figure here is calculated directly from the dataset. The narrative layer
// (narrativeGenerator.js) is only ever given this object — never the raw
// rows — and is instructed to treat it as ground truth it cannot alter.

function fmt(n) {
  if (n === null || n === undefined || isNaN(n)) return null;
  return Number(n);
}

function pickPrimaryDimension(schema) {
  const excluded = new Set([
    ...(schema.time || []),
    ...(schema.ids || []),
    ...(schema.people || []),
  ]);
  return (schema.dimensions || []).find(d => !excluded.has(d)) || null;
}

function directionLabel(pctChange) {
  if (pctChange === null || pctChange === undefined) return "unknown";
  if (Math.abs(pctChange) < 0.5) return "stable";
  return pctChange > 0 ? "increased" : "decreased";
}

// Builds a period-by-period table with absolute + percentage change vs the
// PREVIOUS period (not just first-vs-last), needed for the year-over-year
// table and for distinguishing correlation from causation later.
function buildPeriodTable(dataset, dateColumn, measure, aggregation) {

  const trend = computeTrend(dataset, { dateColumn, measure, period: "year", aggregation });

  if (!trend.success || trend.series.length < 1) return null;

  return trend.series.map((p, i) => {

    const prev = i > 0 ? trend.series[i - 1] : null;

    const absoluteChange = prev && p.value !== null && prev.value !== null
      ? p.value - prev.value
      : null;

    const pctChange = prev && absoluteChange !== null && prev.value
      ? (absoluteChange / Math.abs(prev.value)) * 100
      : null;

    return {
      period: p.period,
      value: p.value,
      absoluteChange,
      pctChange,
      direction: i === 0 ? "baseline" : directionLabel(pctChange),
    };
  });
}

// Ranked performance + concentration metrics for a categorical dimension
// (used for both "sales representative performance" and generic dimension
// breakdowns).
function buildDimensionBreakdown(dataset, dimension, measure) {

  const grouped = groupBy(dataset, dimension, measure, "sum")
    .filter(g => g.value !== null)
    .sort((a, b) => b.value - a.value);

  if (!grouped.length) return null;

  const counts = groupBy(dataset, dimension, measure, "count");
  const countMap = Object.fromEntries(counts.map(c => [c.group, c.value]));

  const total = grouped.reduce((a, g) => a + g.value, 0);
  const top = grouped[0].value;

  const rows = grouped.map((g, i) => ({
    rank: i + 1,
    label: String(g.group),
    value: g.value,
    share: total ? (g.value / total) * 100 : null,
    diffFromTop: i === 0 ? 0 : ((g.value - top) / Math.abs(top)) * 100,
    count: countMap[g.group] ?? null,
    average: countMap[g.group] ? g.value / countMap[g.group] : null,
  }));

  const cumulative = (n) =>
    rows.slice(0, n).reduce((a, r) => a + (r.share || 0), 0);

  return {
    dimension,
    measure,
    rows,
    count: rows.length,
    concentration: {
      top1Share: cumulative(1),
      top2Share: cumulative(2),
      top3Share: cumulative(3),
      entityCount: rows.length,
    },
  };
}

export function buildMetrics(dataset, schema, datasetName) {

  const primaryMeasure = schema?.measures?.[0] || null;
  const secondaryMeasure = schema?.measures?.[1] || null;
  const dateColumn = schema?.time?.[0] || null;
  const repDimension = schema?.people?.[0] || null;
  const primaryDimension = pickPrimaryDimension(schema || {});

  // ---- Meta / analysis scope ----
  const meta = {
    datasetName,
    generatedAt: new Date().toISOString(),
    rowCount: dataset.length,
    columnCount: schema?.columns?.length || 0,
    analysisScope: {
      measures: schema?.measures || [],
      dimensions: (schema?.dimensions || []).filter(d => !(schema?.time || []).includes(d)),
      time: schema?.time || [],
    },
  };

  // ---- Time period range ----
  let timePeriod = null;
  let timeSeries = null;
  let overallChange = null;
  let countSeries = null;
  let averageSeries = null;

  if (dateColumn && primaryMeasure) {

    timeSeries = buildPeriodTable(dataset, dateColumn, primaryMeasure, "sum");

    const countTrend = computeTrend(dataset, { dateColumn, measure: primaryMeasure, period: "year", aggregation: "count" });
    const avgTrend = computeTrend(dataset, { dateColumn, measure: primaryMeasure, period: "year", aggregation: "average" });

    countSeries = countTrend.success ? countTrend.series : null;
    averageSeries = avgTrend.success ? avgTrend.series : null;

    if (timeSeries?.length) {

      timePeriod = {
        column: dateColumn,
        start: timeSeries[0].period,
        end: timeSeries[timeSeries.length - 1].period,
      };

      const first = timeSeries[0].value;
      const last = timeSeries[timeSeries.length - 1].value;
      const absoluteChange = (first !== null && last !== null) ? last - first : null;
      const pctChange = (absoluteChange !== null && first) ? (absoluteChange / Math.abs(first)) * 100 : null;

      overallChange = {
        first,
        last,
        firstPeriod: timeSeries[0].period,
        lastPeriod: timeSeries[timeSeries.length - 1].period,
        absoluteChange,
        pctChange,
        direction: directionLabel(pctChange),
      };
    }
  }

  // ---- Primary measure summary ----
  const primary = primaryMeasure ? {
    measure: primaryMeasure,
    total: fmt(buildAggregateResult("sum", dataset, primaryMeasure).value),
    average: fmt(buildAggregateResult("average", dataset, primaryMeasure).value),
    count: dataset.length,
  } : null;

  const secondary = secondaryMeasure ? {
    measure: secondaryMeasure,
    total: fmt(buildAggregateResult("sum", dataset, secondaryMeasure).value),
    average: fmt(buildAggregateResult("average", dataset, secondaryMeasure).value),
  } : null;

  // ---- Sales representative / person performance ----
  const repPerformance = (repDimension && primaryMeasure)
    ? buildDimensionBreakdown(dataset, repDimension, primaryMeasure)
    : null;

  // ---- Additional dimension breakdowns (excluding rep + time) ----
  const excludedForBreakdown = new Set([repDimension, dateColumn].filter(Boolean));

  const candidateDimensions = (schema?.dimensions || [])
    .filter(d => !excludedForBreakdown.has(d) && !(schema?.ids || []).includes(d))
    .slice(0, 4); // cap — only meaningful ones, not every column

  const dimensionBreakdowns = primaryMeasure
    ? candidateDimensions
        .map(dim => buildDimensionBreakdown(dataset, dim, primaryMeasure))
        .filter(b => b && b.rows.length > 1) // a dimension with 1 unique value isn't useful to analyze
    : [];

  // ---- Correlation between the two strongest measures (supporting
  //      evidence only — never presented as causal on its own) ----
  let measureCorrelation = null;

  if (primaryMeasure && secondaryMeasure) {
    const corr = computeCorrelation(dataset, { columnX: primaryMeasure, columnY: secondaryMeasure });
    if (corr.success) measureCorrelation = corr;
  }

  // ---- Data quality ----
  const missingValueColumns = (schema?.columns || [])
    .map(col => {
      const missing = dataset.filter(r => r[col] === "" || r[col] === null || r[col] === undefined).length;
      return { column: col, missingCount: missing, missingPercent: (missing / dataset.length) * 100 };
    })
    .filter(c => c.missingCount > 0);

  return {
    meta,
    primary,
    secondary,
    primaryDimension,
    timePeriod,
    timeSeries,
    countSeries,
    averageSeries,
    overallChange,
    repPerformance,
    dimensionBreakdowns,
    measureCorrelation,
    dataQuality: { missingValueColumns },
  };
}
